/**
 * V2 Upsell modal — intercepts V2 Product Hero ATC when enabled,
 * then adds the page product ± upsell product and sends the shopper to checkout.
 */
(function () {
  if (window.__v2UpsellModalReady) return;
  window.__v2UpsellModalReady = true;

  const MODAL_SELECTOR = '[data-v2-upsell-modal]';
  const HERO_FORM_SELECTOR = '.v2-product-hero__form';
  const HERO_TRIGGER = '[data-v2-product-hero][data-v2-upsell-trigger]';

  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function addUrl() {
    return `${cartRoot()}cart/add.js`;
  }

  function checkoutUrl(discountCode) {
    const url = `${cartRoot()}checkout`;
    if (!discountCode) return url;
    return `${url}?discount=${encodeURIComponent(discountCode)}`;
  }

  function parseId(value) {
    const id = parseInt(value, 10);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  function modalRoot(fromEl) {
    return fromEl && fromEl.closest ? fromEl.closest(MODAL_SELECTOR) : null;
  }

  function drawerOf(modal) {
    return modal ? modal.querySelector('[data-v2-drawer]') : null;
  }

  function selectedUpsellId(modal) {
    const type = modal.getAttribute('data-v2-upsell-type') || 'choice';
    if (type === 'single') {
      return parseId(modal.getAttribute('data-v2-upsell-single-variant'));
    }

    const selected = modal.querySelector('[data-v2-upsell-chip][aria-checked="true"]');
    if (selected) return parseId(selected.getAttribute('data-v2-upsell-variant'));

    const first = modal.querySelector('[data-v2-upsell-chip]');
    return first ? parseId(first.getAttribute('data-v2-upsell-variant')) : 0;
  }

  function buildItems(modal, includeUpsell) {
    const mainId = parseId(modal.getAttribute('data-v2-upsell-main-variant'));
    if (!mainId) return [];

    if (!includeUpsell) {
      return [{ id: mainId, quantity: 1 }];
    }

    const upsellId = selectedUpsellId(modal);
    if (!upsellId || upsellId === mainId) {
      return [{ id: mainId, quantity: upsellId === mainId ? 2 : 1 }];
    }

    return [
      { id: mainId, quantity: 1 },
      { id: upsellId, quantity: 1 },
    ];
  }

  async function addItems(items) {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: items }),
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    return response.json();
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function closeModal(modal) {
    if (!modal || !window.V2Drawer) return;
    window.V2Drawer.close(drawerOf(modal));
  }

  async function goToCheckout(modal, includeUpsell) {
    if (!modal || modal.classList.contains('is-busy')) return;

    if (isDesignMode()) {
      closeModal(modal);
      return;
    }

    const items = buildItems(modal, includeUpsell);
    if (!items.length) return;

    modal.classList.add('is-busy');
    modal.setAttribute('aria-busy', 'true');

    const discount = includeUpsell
      ? (modal.getAttribute('data-v2-upsell-discount') || '').trim()
      : '';

    try {
      await addItems(items);
      window.location.href = checkoutUrl(discount);
    } catch (error) {
      modal.classList.remove('is-busy');
      modal.removeAttribute('aria-busy');
    }
  }

  function selectChip(modal, chip) {
    if (!modal || !chip) return;

    modal.querySelectorAll('[data-v2-upsell-chip]').forEach(function (el) {
      const on = el === chip;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    const key = chip.getAttribute('data-v2-upsell-key');
    modal.querySelectorAll('[data-v2-upsell-image]').forEach(function (img) {
      img.classList.toggle('is-active', img.getAttribute('data-v2-upsell-image') === key);
    });
  }

  function moveChip(chip, direction) {
    const group = chip.closest('[role="radiogroup"]');
    if (!group) return;
    const chips = Array.prototype.slice.call(group.querySelectorAll('[data-v2-upsell-chip]'));
    const index = chips.indexOf(chip);
    if (index < 0) return;
    const next = chips[(index + direction + chips.length) % chips.length];
    if (!next) return;
    selectChip(modalRoot(chip), next);
    next.focus();
  }

  function openModal(modal) {
    if (!modal || !window.V2Drawer) return;
    const drawer = drawerOf(modal);
    if (!drawer) return;
    window.V2Drawer.open(drawer);
  }

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches(HERO_FORM_SELECTOR)) return;

    const hero = form.closest(HERO_TRIGGER);
    if (!hero) return;

    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal) return;

    event.preventDefault();
    openModal(modal);
  });

  document.addEventListener('keydown', function (event) {
    const chip = event.target.closest('[data-v2-upsell-chip]');
    if (!chip) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      moveChip(chip, 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveChip(chip, -1);
    }
  });

  document.addEventListener('click', function (event) {
    const chip = event.target.closest('[data-v2-upsell-chip]');
    if (chip) {
      event.preventDefault();
      selectChip(modalRoot(chip), chip);
      return;
    }

    const accept = event.target.closest('[data-v2-upsell-accept]');
    if (accept) {
      event.preventDefault();
      goToCheckout(modalRoot(accept), true);
      return;
    }

    const decline = event.target.closest('[data-v2-upsell-decline]');
    if (decline) {
      event.preventDefault();
      goToCheckout(modalRoot(decline), false);
    }
  });

  document.addEventListener('v2-drawer:dismiss', function (event) {
    const drawer = event.target.closest ? event.target.closest('[data-v2-drawer]') : null;
    const modal = drawer ? modalRoot(drawer) || drawer.closest(MODAL_SELECTOR) : null;
    if (!modal) return;
    closeModal(modal);
  });

  document.addEventListener('shopify:section:select', function (event) {
    const modal = event.target && event.target.querySelector
      ? event.target.querySelector(MODAL_SELECTOR)
      : null;
    if (modal) openModal(modal);
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    const modal = event.target && event.target.querySelector
      ? event.target.querySelector(MODAL_SELECTOR)
      : null;
    if (modal) closeModal(modal);
  });
})();
